import { useCallback, useMemo, useState } from "react";
import { Client } from "creso-core/types";
import {
  useGenerateSignRequest,
  useGetFolioNumbers,
  useGiftSubmissions,
} from "creso-core/hooks";
import {
  Badge,
  Button,
  buttonVariants,
  cn,
  Dialog,
  DialogContent,
  DialogTrigger,
} from "web-design-system";
import { AmcLogo } from "web-core";
import { handleError } from "@/utils/error";
import { toast } from "sonner";
import ThrottledButton from "@core/components/throttledButton";
import { InfoIcon } from "lucide-react";
import { GiftKycForm } from "./kyc/gift-kyc-form";
import { GiftFlowHeader } from "./gift-flow-header";
import { GiftAmc } from "../constants";
import { useGiftAmcs } from "../hooks/use-gift-amcs";
import { getLatestGiftSubmission } from "../utils";

type AmcOnboardingState =
  | "not-started"
  | "notified"
  | "in-progress"
  | "can-transact";

export interface GiftAmcSelectionProps {
  client: Client;
  onTransact: (amc: GiftAmc, folioNumber: string) => void;
}

export function GiftAmcSelection({
  client,
  onTransact,
}: GiftAmcSelectionProps) {
  const { data: submissions, isLoading } = useGiftSubmissions(
    client.investment_account || ""
  );

  const { data: folioNumbers } = useGetFolioNumbers({
    investment_account_guid: client.investment_account || "",
    giftOnly: true,
  });

  const giftAmcs = useGiftAmcs();

  const rows = useMemo(() => {
    return giftAmcs.map((amc) => {
      const amcSubmissions =
        submissions?.filter((s) => s.amc_code === amc.amc_code) || [];
      const signedSubmissions = amcSubmissions.filter(
        (s) => s.signing_status === "signed"
      );
      const folioNumber = folioNumbers?.find((f) => f.amc_id === amc.amc_id);

      let state: AmcOnboardingState = "not-started";
      if (folioNumber) {
        state = "can-transact";
      } else if (signedSubmissions.length > 0) {
        state = "in-progress";
      } else if (amcSubmissions.length > 0) {
        state = "notified";
      }

      return { amc, state, folioNumber: folioNumber?.folio_number };
    });
  }, [giftAmcs, submissions, folioNumbers]);

  const { mutateAsync: sendSignRequest } = useGenerateSignRequest(true);

  const onNotify = useCallback(
    async (amc: GiftAmc) => {
      try {
        const latestPendingForm = getLatestGiftSubmission(
          submissions,
          (s) => s.amc_code === amc.amc_code && s.signing_status === "pending"
        );
        if (!latestPendingForm) {
          throw new Error("No pending forms found");
        }
        await sendSignRequest(latestPendingForm.form_id);
        toast.success("Signing email sent successfully.");
      } catch (e) {
        handleError(e, "Failed to send signing link.");
      }
    },
    [sendSignRequest, submissions]
  );

  const liveRows = rows.filter(({ amc }) => amc.enabled);
  const otherRows = rows.filter(({ amc }) => !amc.enabled);

  const [selectedMsCode, setSelectedMsCode] = useState<string>(
    liveRows?.[0].amc.ms_code
  );
  const [createFolioOpen, setCreateFolioOpen] = useState(false);
  const selectedRow = rows.find((row) => row.amc.ms_code === selectedMsCode);

  if (isLoading) return null;

  return (
    <div className="flex flex-col h-full min-h-0">
      <GiftFlowHeader
        title="Gift Outbound Transaction"
        step={1}
        totalSteps={3}
      />

      <div className="flex-1 min-h-0 overflow-y-auto pt-6 flex flex-col gap-5">
        {liveRows.length > 0 && (
          <div className="flex flex-col gap-3">
            <span className="text-sm font-semibold">Live AMCs</span>
            {liveRows.map((row) => (
              <GiftAmcRow
                key={row.amc.ms_code}
                {...row}
                selected={row.amc.ms_code === selectedMsCode}
                onSelect={() => setSelectedMsCode(row.amc.ms_code)}
              />
            ))}
          </div>
        )}

        <div className="h-px bg-border" />

        {otherRows.length > 0 && (
          <div className="flex flex-col gap-3">
            <span className="text-sm font-semibold">
              Other AMCs with outbound funds
            </span>
            {otherRows.map((row) => (
              <GiftAmcRow
                key={row.amc.ms_code}
                {...row}
                selected={row.amc.ms_code === selectedMsCode}
                onSelect={() => setSelectedMsCode(row.amc.ms_code)}
              />
            ))}
          </div>
        )}
      </div>

      <div className="pt-6">
        {selectedRow?.state === "not-started" ? (
          <Dialog open={createFolioOpen} onOpenChange={setCreateFolioOpen}>
            <DialogTrigger
              className={cn(buttonVariants(), "w-full")}
              disabled={!selectedRow.amc.enabled}
            >
              + Create Folio
            </DialogTrigger>
            <DialogContent
              noOverlay
              className="p-5 max-w-none w-[75vw] h-[85vh]"
            >
              <GiftKycForm
                initialStep={1}
                client={client}
                amc={selectedRow.amc.kyc_code}
                signingEnabled
              />
            </DialogContent>
          </Dialog>
        ) : selectedRow?.state === "notified" ? (
          <ThrottledButton
            onClick={() => onNotify(selectedRow.amc)}
            className="w-full"
            beginOnMount={false}
            throttle={25}
          >
            Notify
          </ThrottledButton>
        ) : (
          <div className="flex flex-col gap-2">
            <Button
              className="w-full"
              disabled={
                !selectedRow ||
                !selectedRow.amc.enabled ||
                selectedRow.state === "in-progress" ||
                !selectedRow.folioNumber
              }
              onClick={() =>
                selectedRow?.folioNumber &&
                onTransact(selectedRow.amc, selectedRow.folioNumber)
              }
            >
              {!selectedRow
                ? "Proceed"
                : selectedRow.state === "in-progress"
                  ? "In progress"
                  : "Transact"}
            </Button>
            {selectedRow?.state === "in-progress" && (
              <div className="text-xs flex items-center justify-center gap-1 text-muted-foreground">
                <InfoIcon className="size-3 mb-[0.5]" />
                Client has signed the form, and folio will be created in 2-3
                days.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function GiftAmcRow({
  amc,
  selected,
  onSelect,
  folioNumber,
}: {
  amc: GiftAmc;
  state: AmcOnboardingState;
  folioNumber?: string | null;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      disabled={!amc.enabled}
      onClick={onSelect}
      className={cn(
        "flex w-full items-center justify-between rounded-lg border p-3 text-left transition-colors",
        amc.enabled ? "hover:border-foreground/40" : "cursor-not-allowed",
        selected && amc.enabled ? "border-foreground" : "border-border"
      )}
    >
      <div className="flex items-center gap-3">
        <AmcLogo size="sm" amc_code={amc.ms_code} />
        <span className={cn("text-sm", selected && "font-semibold")}>
          {amc.name}
        </span>
      </div>

      {folioNumber && (
        <span className="font-semibold text-sm">#{folioNumber}</span>
      )}

      {!amc.enabled && (
        <Badge
          variant="custom"
          className="bg-tag-grey-foreground px-2 py-1 font-normal rounded text-tag-grey"
        >
          Coming soon
        </Badge>
      )}
    </button>
  );
}
